import { defer, merge, type Observable } from 'rxjs'
import { localStorage } from '@/infra/storage'
import type { PageActivityDescriptor } from '@/infra/page-activity'
import type { KeyPress } from '@/utils/keyboard-util'
import { ValueRetrievablePromise } from '@/utils/value-retrievable-promise'
import { ShellFeature } from '@/features/shell'
import type { Location } from 'react-router'
import { IamFeature } from '@/features/iam'
import { SoundFeature } from '@/features/sound'
import { FeatureTogglesFeature } from '@/features/feature-toggles'
import { RelationManagerFeature } from '@/features/relation-manager'
import { FreshbooksFeature } from '@/features/freshbooks'
import { TrackingFeature } from '@/features/tracking'
import { MessagingFeature } from '@/features/messaging'
import { NotificationsFeature } from '@/features/notifications'
import { DocumentsFeature } from '@/features/documents'
import { BillingFeature } from '@/features/billing'
import { InventoryFeature } from '@/features/inventory'
import { OpportunitiesFeature } from '@/features/opportunities'
import { JobsFeature } from '@/features/jobs'
import { PayoutsFeature } from '@/features/payouts'
import { AdminFeature } from '@/features/admin'
import { UserManagementFeature } from '@/features/user-management'
import { ClientUserManagementFeature } from '@/features/client-user-management'
import { CallCenterFeature } from '@/features/call-center'
import { LegendsFeature } from '@/features/legends'

/**
 * Defines a function for navigating via the router.
 */
export type NavigateFn = (
  to: string,
  options?: { replace?: boolean; viewTransition?: boolean },
) => void

/**
 * Defines a function for reading the router's current location.
 */
export type GetLocationFn = () => Location

/**
 * Options for the root store.
 */
export interface RootStoreOptions {
  /**
   * Provides a way to navigate via the router.
   */
  navigate: NavigateFn

  /**
   * Returns the router's current location.
   */
  getLocation: GetLocationFn

  /**
   * Returns the current time.
   */
  getTime: () => Date

  /**
   * Observable stream of key presses on the document.
   */
  keyboard$: Observable<KeyPress>

  /**
   * Observable stream for page activity. Emits the current one on subscribe.
   */
  pageActivity$: Observable<PageActivityDescriptor>

  /**
   * Observable stream that emits whether the current user is considered active
   * or inactive.
   */
  userActive$: Observable<boolean>
}

/**
 * The root store, which ties together all the features.
 *
 * Features are created in dependency order. A feature that needs one created
 * after it gets a closure, so that the dependency resolves on first use.
 */
export class RootStore {
  readonly iam
  readonly shell
  readonly featureToggles
  readonly sound
  readonly relationManager
  readonly freshbooks
  readonly tracking
  readonly messaging
  readonly notifications
  readonly documents
  readonly billing
  readonly inventory
  readonly opportunities
  readonly jobs
  readonly payouts
  readonly admin
  readonly userManagement
  readonly clientUserManagement
  readonly callCenter
  readonly legends

  constructor(options: RootStoreOptions) {
    this.iam = IamFeature({
      navigate: options.navigate,
      getLocation: options.getLocation,
      getAuthTokenDelegate: () => this.shell.getAuthTokenDelegate(),
      newAlert: () => this.shell.newAlert(),
      createAlertErrorHandler: (title) =>
        this.shell.createAlertErrorHandler(title),
      createAlertRetryWrapper: (title, signal) =>
        this.shell.createAlertRetryWrapper(title, signal),
      onlineStatusForUser: (userPublicId) =>
        this.messaging.onlineStatusForUser(userPublicId),
      isBillingEnabled: () => this.billing.isBillingEnabled(),
      createProfilePaymentMethodsViewModel: ValueRetrievablePromise.defer(
        () => this.billing.createProfilePaymentMethodsViewModel,
      ),
      createTeamPaymentsViewModel: ValueRetrievablePromise.defer(
        () => this.billing.createTeamPaymentsViewModel,
      ),
      createStripeConnectCallbackModalViewModel: ValueRetrievablePromise.defer(
        () => this.billing.createStripeConnectCallbackModalViewModel,
      ),
      isPayoutsEnabled: () => this.payouts.isEnabled(),
      createProfilePayoutMethodViewModel: ValueRetrievablePromise.defer(
        () => this.payouts.createProfilePayoutMethodViewModel,
      ),
      fetchNotificationPreferences: (member) =>
        this.notifications.fetchNotificationPreferences(member),
      saveNotificationPreferences: (member, preferences) =>
        this.notifications.saveNotificationPreferences(member, preferences),
    })

    this.shell = ShellFeature({
      keyboard$: options.keyboard$,
      getCurrentWorkspace: this.iam.getCurrentWorkspace,
      findCurrentWorkspace: this.iam.findCurrentWorkspace,
      getCurrentMember: this.iam.getCurrentMember,
      getCurrentUser: this.iam.getCurrentUser,
      getCurrentUserWorkspaceMemberships:
        this.iam.getCurrentUserWorkspaceMemberships,
      getTeamMembersForWorkspaceMember:
        this.iam.getTeamMembersForWorkspaceMember,
      getUserData: this.iam.getUserData,
      currentMember$: this.iam.currentMember$,
      readServerRenderedWorkspace: this.iam.readServerRenderedWorkspace,
      hasMultipleWorkspaces: this.iam.hasMultipleWorkspaces,
      isWorkspaceSelectorOpen: this.iam.isWorkspaceSelectorOpen,
      toggleWorkspaceSelector: this.iam.toggleWorkspaceSelector,
      openWorkspaceSearch: this.iam.openWorkspaceSearch,
      isEnrolledInMultiFactorAuth: this.iam.isEnrolledInMultiFactorAuth,
      getMultiFactorAuthEnrollmentUrl: this.iam.getMultiFactorAuthEnrollmentUrl,
      getEnterpriseConnection: this.iam.getEnterpriseConnection,
      promptCreateJob: (prefill) => this.jobs.promptCreateJob(prefill),
      unreadMessagesCount$: defer(() => this.messaging.totalUnreadCount$),
      canViewOpportunities$: defer(
        () => this.opportunities.canViewOpportunities$,
      ),
      opportunityCount$: defer(() => this.opportunities.opportunityCount$),
      canViewProPayouts$: defer(() => this.payouts.canViewProPayouts$),
      canViewPayoutsAdmin: () => this.payouts.canViewPayoutsAdmin(),
      canViewLegends: () => this.legends.canViewLegends(),
      unreadNotificationsCount$: defer(() => this.notifications.unreadCount$),
      toggleNotificationsSidebar: () =>
        void this.notifications.toggleNotificationsSidebar(),
    })

    this.featureToggles = FeatureTogglesFeature({
      currentUser$: this.iam.currentUser$,
      currentUserData$: this.iam.currentUserData$,
    })

    this.sound = SoundFeature({
      storage: localStorage,
    })

    this.relationManager = RelationManagerFeature({
      token$: this.iam.token$,
      primeMemberByPublicId: this.iam.primeMemberByPublicId,
    })

    this.freshbooks = FreshbooksFeature({
      token$: this.iam.token$,
    })

    this.tracking = TrackingFeature({
      token$: this.iam.token$,
      currentMember$: this.iam.currentMember$,
      currentUserData$: this.iam.currentUserData$,
      logout$: this.iam.logout$,
      fetchHubspotToken: this.freshbooks.fetchHubspotToken,
    })

    this.messaging = MessagingFeature({
      token$: this.iam.token$,
      pageActivity$: options.pageActivity$,
      userActive$: options.userActive$,
      currentUser$: this.iam.currentUser$,
      getCurrentUser: this.iam.getCurrentUser,
      getCurrentMember: this.iam.getCurrentMember,
      primeWorkspace: this.iam.primeWorkspace,
      primeMemberByPublicId: this.iam.primeMemberByPublicId,
      primeUser: this.iam.primeUser,
      jobProgression: (jobId) => this.jobs.jobProgression(jobId),
      playSound: this.sound.playSound,
      newAlert: this.shell.newAlert,
      createAlertErrorHandler: this.shell.createAlertErrorHandler,
      createAlertRetryWrapper: this.shell.createAlertRetryWrapper,
      getExternalUrlDelegate: this.shell.getExternalUrlDelegate,
    })

    this.notifications = NotificationsFeature({
      token$: this.iam.token$,
      navigate: options.navigate,
      getTime: options.getTime,
      createAlertErrorHandler: this.shell.createAlertErrorHandler,
      createAlertRetryWrapper: this.shell.createAlertRetryWrapper,
      currentUser$: this.iam.currentUser$,
      currentMember$: this.iam.currentMember$,
      primeMember: this.iam.primeMember,
      getCurrentUserWorkspaceMemberships:
        this.iam.getCurrentUserWorkspaceMemberships,
      job: (id) => this.jobs.job(id),
      fetchJob: (id) => this.jobs.fetchJob(id),
      systemNotifications$: defer(() =>
        merge(
          this.jobs.systemNotifications$,
          this.opportunities.systemNotifications$,
        ),
      ),
    })

    this.documents = DocumentsFeature({
      token$: this.iam.token$,
      newAlert: this.shell.newAlert,
      createAlertErrorHandler: this.shell.createAlertErrorHandler,
      createAlertRetryWrapper: this.shell.createAlertRetryWrapper,
      getDocumentDownloadDelegate: this.shell.getDocumentDownloadDelegate,
      currentMember$: this.iam.currentMember$,
      getCurrentMember: this.iam.getCurrentMember,
      getCurrentUserWorkspaceMemberships:
        this.iam.getCurrentUserWorkspaceMemberships,
      getWorkspaceSelection: this.iam.getWorkspaceSelection,
      primeMemberByPublicId: this.iam.primeMemberByPublicId,
      primeWorkspace: this.iam.primeWorkspace,
      fetchJob: (id) => this.jobs.fetchJob(id),
      fetchJobs: (...args) => this.jobs.fetchJobs(...args),
      searchJobs: (...args) => this.jobs.searchJobs(...args),
    })

    this.billing = BillingFeature({
      token$: this.iam.token$,
      currentMember$: this.iam.currentMember$,
      getCurrentWorkspace: this.iam.getCurrentWorkspace,
      getCurrentUser: this.iam.getCurrentUser,
      getCurrentMember: this.iam.getCurrentMember,
      getTeamMember: this.iam.getTeamMember,
      primeWorkspace: this.iam.primeWorkspace,
      createAlertErrorHandler: this.shell.createAlertErrorHandler,
      createAlertRetryWrapper: this.shell.createAlertRetryWrapper,
    })

    this.inventory = InventoryFeature({
      token$: this.iam.token$,
      primeWorkspace: this.iam.primeWorkspace,
      primeUser: this.iam.primeUser,
      primeDocumentTypeByShortId: this.documents.primeDocumentTypeByShortId,
      fetchDocumentTypes: this.documents.fetchDocumentTypes,
      newAlert: this.shell.newAlert,
      createAlertErrorHandler: this.shell.createAlertErrorHandler,
      createAlertRetryWrapper: this.shell.createAlertRetryWrapper,
      getCurrentUserWorkspaceMemberships:
        this.iam.getCurrentUserWorkspaceMemberships,
    })

    this.opportunities = OpportunitiesFeature({
      token$: this.iam.token$,
      workRealtime$: defer(() => this.jobs.workRealtime$),
      navigate: options.navigate,
      newAlert: this.shell.newAlert,
      createAlertRetryWrapper: this.shell.createAlertRetryWrapper,
      createAlertErrorHandler: this.shell.createAlertErrorHandler,
      currentUser$: this.iam.currentUser$,
      currentMember$: this.iam.currentMember$,
      getCurrentMember: this.iam.getCurrentMember,
      primeUser: this.iam.primeUser,
      primeWorkspace: this.iam.primeWorkspace,
      getTeamMembersForWorkspaceMember:
        this.iam.getTeamMembersForWorkspaceMember,
      getUserData: this.iam.getUserData,
      fetchUserData: this.iam.fetchUserData,
      getCurrentUserWorkspaceMemberships:
        this.iam.getCurrentUserWorkspaceMemberships,
      listWorkspaceMemberships: this.iam.listWorkspaceMemberships,
      pickupOpportunity: (opportunityId) =>
        this.jobs.pickupOpportunity(opportunityId),
      acceptOffer: (id) => this.jobs.acceptOffer(id),
      fetchProOpenJobCounts: (userPublicId) =>
        this.jobs.fetchProOpenJobCounts(userPublicId),
      fetchBookedConsultations: () => this.jobs.fetchBookedConsultations(),
      pickupConsultationJob: (...args) =>
        this.jobs.pickupConsultationJob(...args),
      jobEvents$: defer(() => this.jobs.jobEvents$),
    })

    this.jobs = JobsFeature({
      token$: this.iam.token$,
      pageActivity$: options.pageActivity$,
      userActive$: options.userActive$,
      navigate: options.navigate,
      newAlert: this.shell.newAlert,
      createAlertErrorHandler: this.shell.createAlertErrorHandler,
      createAlertRetryWrapper: this.shell.createAlertRetryWrapper,
      renameShortcut$: this.shell.renameShortcut$,
      getAddToCalendarDelegate: this.shell.getAddToCalendarDelegate,
      currentMember$: this.iam.currentMember$,
      getCurrentUser: this.iam.getCurrentUser,
      getCurrentMember: this.iam.getCurrentMember,
      primeUser: this.iam.primeUser,
      primeWorkspace: this.iam.primeWorkspace,
      fetchWorkspace: this.iam.fetchWorkspace,
      getWorkspaceSelection: this.iam.getWorkspaceSelection,
      getCurrentUserWorkspaceMemberships:
        this.iam.getCurrentUserWorkspaceMemberships,
      listWorkspaceMemberships: this.iam.listWorkspaceMemberships,
      primeMember: this.iam.primeMember,
      primeMemberByPublicId: this.iam.primeMemberByPublicId,
      fetchMember: this.iam.fetchMember,
      fetchMembersByPublicIds: this.iam.fetchMembersByPublicIds,
      searchMembers: this.iam.searchMembers,
      getWorkspaceMembers: this.iam.getWorkspaceMembers,
      primeTeam: this.iam.primeTeam,
      getTeam: this.iam.getTeam,
      listUserTeams: this.iam.listUserTeams,
      getTeamMember: this.iam.getTeamMember,
      filterTeamMembers: this.iam.filterTeamMembers,
      getTeamMembersForWorkspaceMember:
        this.iam.getTeamMembersForWorkspaceMember,
      fetchTeamMember: this.iam.fetchTeamMember,
      listUserTeamMembers: this.iam.listUserTeamMembers,
      listUserTeamMembersForWorkspace: this.iam.listUserTeamMembersForWorkspace,
      listTeamMembers: this.iam.listTeamMembers,
      listTeamMembersForWorkspaceMember:
        this.iam.listTeamMembersForWorkspaceMember,
      getUserData: this.iam.getUserData,
      fetchUserData: this.iam.fetchUserData,
      ensureTermsAccepted: this.iam.ensureTermsAccepted,
      primeConversation: this.messaging.primeConversation,
      primeRoom: this.messaging.primeRoom,
      newMessages$: this.messaging.newMessages$,
      roomEvents$: this.messaging.roomEvents$,
      unreadJobIds$: this.messaging.unreadJobIds$,
      conversation: this.messaging.conversation,
      fetchHumanMessagesTotalCount: this.messaging.fetchHumanMessagesTotalCount,
      createConversationViewModel: this.messaging.createConversationViewModel,
      createRoomViewModel: this.messaging.createRoomViewModel,
      createMessageBankDialogViewModel:
        this.messaging.createMessageBankDialogViewModel,
      desktopNotificationState$: this.notifications.desktopNotificationState$,
      setDesktopNotificationsEnabled:
        this.notifications.setDesktopNotificationsEnabled,
      primeDocument: this.documents.primeDocument,
      searchDocuments: this.documents.searchDocuments,
      archiveDocument: this.documents.archiveDocument,
      downloadDocument: this.documents.downloadDocument,
      downloadDocuments: this.documents.downloadDocuments,
      confirmDocument: this.documents.confirmDocument,
      showDocumentDetails: this.documents.showDocumentDetails,
      jobDocuments: this.documents.jobDocuments,
      documentManualVerificationRequired$:
        this.documents.documentManualVerificationRequired$,
      primeDocumentTypeByShortId: this.documents.primeDocumentTypeByShortId,
      documentTypes$: this.documents.documentTypes$,
      fetchDocumentTypes: this.documents.fetchDocumentTypes,
      createDocumentPickerViewModel:
        this.documents.createDocumentPickerViewModel,
      createUploadBarViewModel: this.documents.createUploadBarViewModel,
      createDocumentCreationModalViewModel:
        this.documents.createDocumentCreationModalViewModel,
      createJobDocumentListViewModel:
        this.documents.createJobDocumentListViewModel,
      createDocumentRequestsViewModel:
        this.documents.createDocumentRequestsViewModel,
      fetchBillingTransactions: this.billing.fetchBillingTransactions,
      billingTransactions: this.billing.billingTransactions,
      fetchBillingProfile: this.billing.fetchBillingProfile,
      canManageBilling: this.billing.canManageBilling,
      billingProfiles$: this.billing.billingProfiles$,
      fetchBillingMethods: this.billing.fetchBillingMethods,
      billingMethods: this.billing.billingMethods,
      changeDefaultBillingMethod: this.billing.changeDefaultBillingMethod,
      promptSelectPaymentMethod: this.billing.promptSelectPaymentMethod,
      promptNewPaymentMethod: this.billing.promptNewPaymentMethod,
      getStripePublishableKey: this.billing.getStripePublishableKey,
      addCardBillingMethod: this.billing.addCardBillingMethod,
      addCoupon: this.billing.addCoupon,
      fetchCoupon: this.billing.fetchCoupon,
      fetchCouponByCode: this.billing.fetchCouponByCode,
      listCoupons: this.billing.listCoupons,
      fetchMemberCoupons: this.billing.fetchMemberCoupons,
      searchServiceItems: this.inventory.listInventoryItems,
      searchInventoryItemsForProvider:
        this.inventory.listInventoryItemsForProvider,
      probeCanViewOpportunities: this.opportunities.probeCanViewOpportunities,
      listQualifiedProviders: this.opportunities.listQualifiedProviders,
      handleOfferResponse: this.opportunities.handleOfferResponse,
      fetchRelationManager: this.relationManager.fetchRelationManager,
      getRelationManager: this.relationManager.getRelationManager,
    })

    this.payouts = PayoutsFeature({
      token$: this.iam.token$,
      newAlert: this.shell.newAlert,
      createAlertErrorHandler: this.shell.createAlertErrorHandler,
      createAlertRetryWrapper: this.shell.createAlertRetryWrapper,
      currentUser$: this.iam.currentUser$,
      getCurrentUser: this.iam.getCurrentUser,
      primeUser: this.iam.primeUser,
      primeWorkspace: this.iam.primeWorkspace,
      listWorkspaces: this.iam.listWorkspaces,
      searchMembers: this.iam.searchMembers,
      fetchUserData: this.iam.fetchUserData,
      primeJob: this.jobs.primeJob,
      fetchJob: this.jobs.fetchJob,
      redirectToStripeConnect: this.billing.redirectToStripeConnect,
    })

    this.admin = AdminFeature({
      token$: this.iam.token$,
      workRealtime$: this.jobs.workRealtime$,
      newAlert: this.shell.newAlert,
      createAlertErrorHandler: this.shell.createAlertErrorHandler,
      createAlertRetryWrapper: this.shell.createAlertRetryWrapper,
      currentMember$: this.iam.currentMember$,
      getCurrentMember: this.iam.getCurrentMember,
      primeUser: this.iam.primeUser,
      primeProvider: this.iam.primeProvider,
      primeWorkspace: this.iam.primeWorkspace,
      primeMemberByPublicId: this.iam.primeMemberByPublicId,
      getCurrentUserWorkspaceMemberships:
        this.iam.getCurrentUserWorkspaceMemberships,
      listWorkspaceMemberships: this.iam.listWorkspaceMemberships,
      primeJob: this.jobs.primeJob,
      fetchJobs: this.jobs.fetchJobs,
      searchInventoryItems: this.inventory.listInventoryItems,
      fetchInventoryItemsByIds: this.inventory.fetchInventoryItemsByIds,
      canViewPayoutsAdmin: this.payouts.canViewPayoutsAdmin,
    })

    this.clientUserManagement = ClientUserManagementFeature({
      newAlert: this.shell.newAlert,
      createAlertErrorHandler: this.shell.createAlertErrorHandler,
      createAlertRetryWrapper: this.shell.createAlertRetryWrapper,
      getCurrentMember: this.iam.getCurrentMember,
      createCustomer: this.iam.createCustomer,
      bulkCreateCustomers: this.iam.bulkCreateCustomers,
      createMemberManually: this.iam.createMemberManually,
      searchMembersByPermission: this.iam.searchMembersByPermission,
      fetchMember: this.iam.fetchMember,
      fetchMembersByPublicIds: this.iam.fetchMembersByPublicIds,
      fetchClientAssignment: this.iam.fetchClientAssignment,
      searchUsers: this.iam.searchUsers,
      createDocumentsTabViewModel: this.documents.createDocumentsTabViewModel,
      promptCreateJob: this.jobs.promptCreateJob,
      createUserJobsTabViewModel: this.jobs.createUserJobsTabViewModel,
    })

    this.userManagement = UserManagementFeature({
      newAlert: this.shell.newAlert,
      createAlertErrorHandler: this.shell.createAlertErrorHandler,
      createAlertRetryWrapper: this.shell.createAlertRetryWrapper,
      getCurrentMember: this.iam.getCurrentMember,
      getCurrentUserWorkspaceMemberships:
        this.iam.getCurrentUserWorkspaceMemberships,
      getWorkspaceSelection: this.iam.getWorkspaceSelection,
      listWorkspaces: this.iam.listWorkspaces,
      joinWorkspaceAsAdmin: this.iam.joinWorkspaceAsAdmin,
      promoteToOwner: this.iam.promoteToOwner,
      searchUsers: this.iam.searchUsers,
      fetchSearchableUser: this.iam.fetchSearchableUser,
      getSearchableUsers: this.iam.getSearchableUsers,
      fetchMember: this.iam.fetchMember,
      createMember: this.iam.createMember,
      createMemberManually: this.iam.createMemberManually,
      searchMembersByPermission: this.iam.searchMembersByPermission,
      updateMember: this.iam.updateMember,
      addMemberRole: this.iam.addMemberRole,
      addMemberSkills: this.iam.addMemberSkills,
      removeMemberSkills: this.iam.removeMemberSkills,
      impersonateMember: this.iam.impersonateMember,
      generateMemberMagicLink: this.iam.generateMemberMagicLink,
      listRoles: this.iam.listRoles,
      getWorkspaceRoles: this.iam.getWorkspaceRoles,
      listSkills: this.iam.listSkills,
      getWorkspaceSkills: this.iam.getWorkspaceSkills,
      listTeams: this.iam.listTeams,
      getWorkspaceTeams: this.iam.getWorkspaceTeams,
      addTeamMember: this.iam.addTeamMember,
      listTeamMembersForWorkspaceMember:
        this.iam.listTeamMembersForWorkspaceMember,
      listUserTeamMembersForWorkspace: this.iam.listUserTeamMembersForWorkspace,
      listTeamRoles: this.iam.listTeamRoles,
      getWorkspaceTeamRoles: this.iam.getWorkspaceTeamRoles,
      fetchUserData: this.iam.fetchUserData,
      updateUserData: this.iam.updateUserData,
      createCustomer: this.iam.createCustomer,
      bulkCreateCustomers: this.iam.bulkCreateCustomers,
      primeProvider: this.iam.primeProvider,
      createProDetailsViewModel: this.iam.createProDetailsViewModel,
      fetchBillingProfile: this.billing.fetchBillingProfile,
      fetchMemberCoupons: this.billing.fetchMemberCoupons,
      createDocumentsTabViewModel: this.documents.createDocumentsTabViewModel,
      fetchJobTotals: this.jobs.fetchJobTotals,
      fetchJobTotalStatusAggregations:
        this.jobs.fetchJobTotalStatusAggregations,
      promptCreateJob: this.jobs.promptCreateJob,
      createUserJobsTabViewModel: this.jobs.createUserJobsTabViewModel,
      createClientAssignmentViewModel:
        this.clientUserManagement.createClientAssignmentViewModel,
      createAdvisorClientsTabViewModel:
        this.clientUserManagement.createAdvisorClientsTabViewModel,
      fetchPayoutMethodInfo: this.payouts.fetchPayoutMethodInfo,
    })

    this.callCenter = CallCenterFeature({
      navigate: options.navigate,
      newAlert: this.shell.newAlert,
      createAlertErrorHandler: this.shell.createAlertErrorHandler,
      createAlertRetryWrapper: this.shell.createAlertRetryWrapper,
      searchCallCenterContacts: this.freshbooks.searchCallCenterContacts,
      searchMembers: this.iam.searchMembers,
      fetchMember: this.iam.fetchMember,
      createMemberManually: this.iam.createMemberManually,
      createCustomer: this.iam.createCustomer,
      getCurrentUser: this.iam.getCurrentUser,
      getUserData: this.iam.getUserData,
      getCurrentUserWorkspaceMemberships:
        this.iam.getCurrentUserWorkspaceMemberships,
      listUserWorkspaceMemberships: this.iam.listUserWorkspaceMemberships,
      fetchWorkspace: this.iam.fetchWorkspace,
      generateCheckoutLink: this.billing.generateCheckoutLink,
      emailCheckoutLink: this.billing.emailCheckoutLink,
      invalidateCheckoutSessions: this.billing.invalidateCheckoutSessions,
      fetchJob: this.jobs.fetchJob,
      searchJobs: this.jobs.searchJobs,
      submitJobRequest: this.jobs.submitJobRequest,
      applyJobCouponByCode: this.jobs.applyJobCouponByCode,
      removeJobCouponByCode: this.jobs.removeJobCouponByCode,
      publishedLegend: this.jobs.publishedLegend,
      createJobCheckoutViewModel: this.jobs.createJobCheckoutViewModel,
      createJobCreationRootViewModel: this.jobs.createJobCreationRootViewModel,
    })

    this.legends = LegendsFeature({
      token$: this.iam.token$,
      navigate: options.navigate,
      newAlert: this.shell.newAlert,
      createAlertErrorHandler: this.shell.createAlertErrorHandler,
      createAlertRetryWrapper: this.shell.createAlertRetryWrapper,
      getCurrentUser: this.iam.getCurrentUser,
      getCurrentUserWorkspaceMemberships:
        this.iam.getCurrentUserWorkspaceMemberships,
      getWorkspaceSelection: this.iam.getWorkspaceSelection,
      primeMember: this.iam.primeMember,
      listRoles: this.iam.listRoles,
      getWorkspaceRoles: this.iam.getWorkspaceRoles,
      listSkills: this.iam.listSkills,
      getWorkspaceSkills: this.iam.getWorkspaceSkills,
      listInventoryItems: this.inventory.listInventoryItems,
      fetchInventoryItemExports: this.inventory.fetchInventoryItemExports,
      importInventoryItems: this.inventory.importInventoryItems,
      listBundles: this.admin.listBundles,
      createQuestionListViewModel: this.jobs.createQuestionListViewModel,
    })
  }
}
